import { Palmtree, Plus, Table2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { useManualTime, useTimesheet, useUsers, type Timesheet } from "@/api/queries";
import { isStaff, useCurrentUser } from "@/app/auth";
import { EmptyState } from "@/components/common/EmptyState";
import { PageHeader } from "@/components/common/PageHeader";
import { VacationBadge } from "@/components/common/VacationBadge";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { NativeSelect } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { dateInZone, formatDuration, formatWeekday, shiftDate, weekStartOf, zonedToISO } from "@/lib/format";
import { cn } from "@/lib/utils";

import { ManualTimeDialog } from "./ManualTimeDialog";
import { ManualTimeList } from "./ManualTimeList";
import { TimesheetTabs } from "./TimesheetTabs";
import { WeekPicker } from "./WeekPicker";

/** The week of the current user or, for staff, of a chosen person. */
export function TimesheetPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "ru";
  const me = useCurrentUser();
  const staff = isStaff(me);
  const [params, setParams] = useSearchParams();
  const today = dateInZone(new Date(), me.timezone);
  const currentWeek = weekStartOf(today);
  const requested = params.get("week") ?? params.get("date");
  const week = requested ? weekStartOf(requested) : currentWeek;
  const userId = (staff && params.get("user")) || undefined;
  const sheet = useTimesheet(userId && userId !== me.id ? userId : undefined, week, shiftDate(week, 6));
  const users = useUsers();
  const [adding, setAdding] = useState(false);
  const data = sheet.data;

  const update = (next: { week?: string; user?: string }) => {
    const p = new URLSearchParams(params);
    p.delete("date");
    if (next.week !== undefined) {
      if (next.week === currentWeek) p.delete("week");
      else p.set("week", next.week);
    }
    if (next.user !== undefined) {
      if (next.user === me.id) p.delete("user");
      else p.set("user", next.user);
    }
    setParams(p, { replace: true });
  };

  const tz = data?.timezone ?? me.timezone;
  const requests = useManualTime({
    view: "mine",
    userId: data?.user.id,
    from: zonedToISO(week, "00:00", tz),
    to: zonedToISO(shiftDate(week, 7), "00:00", tz),
  });
  const others = data && data.user.id !== me.id;

  return (
    <>
      <PageHeader
        title={t("nav.timesheets")}
        actions={
          <>
            {staff && (
              <NativeSelect
                aria-label={t("timesheets.employee")}
                value={userId ?? me.id}
                onChange={(e) => update({ user: e.target.value })}
              >
                {(users.data ?? [me])
                  .filter((u) => u.status !== "invited")
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.full_name}
                    </option>
                  ))}
              </NativeSelect>
            )}
            <WeekPicker week={week} currentWeek={currentWeek} onChange={(w) => update({ week: w })} />
            {data?.can_add_time && (
              <Button onClick={() => setAdding(true)}>
                <Plus /> {t("manual.add.button")}
              </Button>
            )}
          </>
        }
      />
      <TimesheetTabs />

      {sheet.isError ? (
        <Card>
          <EmptyState
            icon={Table2}
            title={t("common.error")}
            action={
              <Button variant="secondary" onClick={() => void sheet.refetch()}>
                {t("common.retry")}
              </Button>
            }
          />
        </Card>
      ) : !data ? (
        <Skeleton className="h-80 w-full" />
      ) : (
        <div className="flex flex-col gap-6">
          <Card className="overflow-hidden">
            <CardHeader className="flex-row flex-wrap items-start justify-between gap-3 pb-4">
              <div>
                <CardTitle className="flex flex-wrap items-center gap-2">
                  {data.user.full_name}
                  <VacationBadge vacation={data.user.vacation} upcoming />
                </CardTitle>
                <CardDescription>
                  {t("timesheets.weekTotal", { time: formatDuration(weekTotal(data), lang) })}
                  {weekManual(data) > 0 &&
                    ` · ${t("timesheets.manualTotal", { time: formatDuration(weekManual(data), lang) })}`}
                </CardDescription>
              </div>
              <Legend />
            </CardHeader>
            <WeekTable data={data} today={dateInZone(new Date(), data.timezone)} lang={lang} />
          </Card>

          <Card className="overflow-hidden">
            <CardHeader className="pb-4">
              <CardTitle>{t("timesheets.requestsTitle")}</CardTitle>
              <CardDescription>
                {data.approval_required
                  ? t("timesheets.requestsHint")
                  : t("timesheets.requestsHintNoApproval")}
              </CardDescription>
            </CardHeader>
            <ManualTimeList
              items={requests.data}
              timeZone={tz}
              empty={{ title: t("timesheets.noRequests") }}
            />
          </Card>
        </div>
      )}

      {adding && data && (
        <ManualTimeDialog
          timeZone={data.timezone}
          projects={data.projects}
          defaultDate={week === currentWeek ? today : shiftDate(week, 4)}
          approvalRequired={data.approval_required}
          forUser={others ? data.user : undefined}
          onClose={() => setAdding(false)}
        />
      )}
    </>
  );
}

function weekTotal(data: Timesheet): number {
  return data.days.reduce((s, d) => s + d.tracked_seconds + d.manual_seconds, 0);
}

function weekManual(data: Timesheet): number {
  return data.days.reduce((s, d) => s + d.manual_seconds, 0);
}

function Legend() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-4 text-xs text-muted">
      <span className="flex items-center gap-1.5">
        <span className="manual-hatch size-2.5 rounded-[3px] bg-primary" aria-hidden="true" />
        {t("timesheets.legendManual")}
      </span>
      <span className="flex items-center gap-1.5">
        <Palmtree className="size-3.5 text-primary" aria-hidden="true" />
        {t("timesheets.legendVacation")}
      </span>
    </div>
  );
}

/** A duration; under a minute is "< 1 min", not "0 min". */
function short(seconds: number, lang: string): string {
  return seconds < 60 ? `< ${formatDuration(60, lang)}` : formatDuration(seconds, lang);
}

function Cell({ tracked, manual, lang }: { tracked: number; manual: number; lang: string }) {
  const { t } = useTranslation();
  if (tracked + manual === 0) return <span className="text-muted">—</span>;
  return (
    <span className="flex flex-col items-end gap-0.5">
      <span className="font-medium">{short(tracked + manual, lang)}</span>
      {manual > 0 && (
        <span className="flex items-center gap-1 text-xs text-muted">
          <span className="manual-hatch size-2 rounded-[2px] bg-primary" aria-hidden="true" />
          {t("timesheets.manualShort", { time: formatDuration(manual, lang) })}
        </span>
      )}
    </span>
  );
}

function WeekTable({ data, today, lang }: { data: Timesheet; today: string; lang: string }) {
  const { t } = useTranslation();
  const hasVacation = data.days.some((d) => d.vacation);
  if (data.rows.length === 0 && !hasVacation) {
    return (
      <EmptyState
        icon={Table2}
        title={t("timesheets.emptyTitle")}
        description={data.can_add_time ? t("timesheets.emptyAddHint") : t("timesheets.emptyDescription")}
      />
    );
  }
  const colClass = (date: string) =>
    cn("px-3 py-2.5 text-right whitespace-nowrap tabular-nums", date === today && "bg-primary-soft/40");
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[900px] text-left">
        <thead className="bg-surface-muted text-xs text-muted">
          <tr>
            <th className="px-5 py-2.5 font-medium">{t("timesheets.columns.task")}</th>
            {data.days.map((d) => (
              <th
                key={d.date}
                className={cn(colClass(d.date), "font-medium", d.date === today && "text-primary")}
              >
                <span className="inline-flex items-center gap-1">
                  {d.vacation && (
                    <Palmtree className="size-3.5 text-primary" aria-label={t("timesheets.legendVacation")} />
                  )}
                  {formatWeekday(d.date, lang)}
                </span>
              </th>
            ))}
            <th className="px-5 py-2.5 text-right font-medium">{t("timesheets.columns.total")}</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row) => (
            <tr key={`${row.project.id}:${row.task?.id ?? ""}`} className="border-t">
              <td className="px-5 py-2.5">
                <div className={cn("font-medium", !row.task && "text-muted")}>
                  {row.task ? row.task.title || "—" : t("manual.noTask")}
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted">
                  <span
                    className="size-2 shrink-0 rounded-full"
                    style={{ backgroundColor: row.project.color }}
                    aria-hidden="true"
                  />
                  {row.project.name}
                </div>
              </td>
              {data.days.map((d, i) => (
                <td key={d.date} className={colClass(d.date)}>
                  <Cell
                    tracked={row.tracked_seconds[i] ?? 0}
                    manual={row.manual_seconds[i] ?? 0}
                    lang={lang}
                  />
                </td>
              ))}
              <td className="px-5 py-2.5 text-right font-semibold tabular-nums">
                {formatDuration(row.total_seconds, lang)}
              </td>
            </tr>
          ))}
          {hasVacation && (
            <tr className="border-t">
              <td className="px-5 py-2.5">
                <div className="font-medium">{t("timesheets.vacationRow")}</div>
                <div className="text-xs text-muted">{t("timesheets.vacationRowHint")}</div>
              </td>
              {data.days.map((d) => (
                <td key={d.date} className={colClass(d.date)}>
                  {d.vacation ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary-soft px-2 py-0.5 text-xs font-medium text-primary">
                      <Palmtree className="size-3" aria-hidden="true" />
                      {t("timesheets.vacationCell")}
                    </span>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
              ))}
              <td />
            </tr>
          )}
        </tbody>
        <tfoot className="border-t bg-surface-muted/60 font-semibold">
          <tr>
            <td className="px-5 py-2.5">{t("timesheets.columns.total")}</td>
            {data.days.map((d) => (
              <td key={d.date} className={colClass(d.date)}>
                <Cell tracked={d.tracked_seconds} manual={d.manual_seconds} lang={lang} />
              </td>
            ))}
            <td className="px-5 py-2.5 text-right tabular-nums">{formatDuration(weekTotal(data), lang)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
